import { Link } from "react-router-dom";
import { events } from "@/mocks/events";
import { highlightStats, hotTags, siteConfig } from "@/mocks/site";
import { statusMeta } from "@/mocks/site";

export default function Hero() {
  const top = events.find((e) => e.id === "e2") || events[0];
  const st = statusMeta[top.status];
  return (
    <section className="relative overflow-hidden border-b border-background-200">
      {/* subtle decorative texture */}
      <div className="pointer-events-none absolute inset-y-0 right-0 hidden w-1/2 lg:block" aria-hidden="true">
        <img
          src="https://readdy.ai/api/search-image?query=large%20soft%20flat%20vector%20pop%20art%20watermelon%20slices%20and%20melon%20shapes%20scattered%20on%20cream%20paper%20background%2C%20muted%20melon%20red%20rind%20green%20and%20warm%20tan%20palette%2C%20light%20and%20airy%2C%20minimal%20decorative%20pattern%2C%20no%20text&width=1600&height=900&seq=hero-bg-melon-2026&orientation=landscape"
          alt=""
          className="h-full w-full object-cover opacity-[0.12]"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-background-50 via-background-50/40 to-background-50/10" />
      </div>

      <div className="relative mx-auto max-w-7xl px-4 md:px-6 py-12 md:py-16 lg:py-20">
        <div className="grid grid-cols-1 gap-10 lg:grid-cols-12 lg:gap-12">
          {/* left copy */}
          <div className="lg:col-span-7 flex flex-col justify-center">
            <span className="inline-flex w-fit items-center gap-2 rounded-full border border-primary-200 bg-primary-50 px-3 py-1 text-xs font-semibold text-primary-700">
              <i className="ri-flashlight-line text-sm" />
              {siteConfig.tagline} · 每日滚动更新
            </span>

            <h1 className="mt-6 font-display text-4xl font-black leading-[1.15] tracking-tight text-foreground-950 md:text-5xl lg:text-[56px]">
              大瓜再多，
              <br />
              也能一次
              <span className="relative inline-block text-primary-600">
                吃明白
                <span className="absolute -bottom-1 left-0 h-1.5 w-full rounded bg-accent-400/50" />
              </span>
            </h1>

            <p className="mt-6 max-w-xl text-base leading-relaxed text-foreground-600 md:text-lg">
              从吃瓜群众视角看热点，把关注度最高的事件、时间线与必看线索整理成可按节奏吃的
              <strong className="font-semibold text-foreground-900">吃瓜指南</strong>
              与<strong className="font-semibold text-foreground-900">瓜单</strong>——进圈三分钟，直接开吃。
            </p>

            <div className="mt-8 flex flex-col gap-3 sm:flex-row sm:items-center">
              <Link
                to="/#gua"
                className="inline-flex whitespace-nowrap items-center justify-center gap-2 rounded-md bg-primary-500 px-6 py-3 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600 cursor-pointer"
              >
                <i className="ri-fire-line text-lg" />
                本期瓜单开吃
              </Link>
              <Link
                to="/#zhinan"
                className="inline-flex whitespace-nowrap items-center justify-center gap-2 rounded-md border border-background-300 bg-background-50 px-6 py-3 text-sm font-semibold text-foreground-800 transition-colors hover:border-primary-300 hover:text-primary-700 cursor-pointer"
              >
                <i className="ri-book-2-line text-lg" />
                先学吃瓜指南
              </Link>
            </div>

            <dl className="mt-10 grid grid-cols-2 gap-x-6 gap-y-5 border-t border-dashed border-background-200 pt-6 sm:grid-cols-4">
              {highlightStats.map((s) => (
                <div key={s.label}>
                  <dt className="sr-only">{s.label}</dt>
                  <dd className="flex items-baseline gap-1">
                    <span className="font-display text-2xl font-black text-accent-700">{s.num}</span>
                    <span className="text-sm font-medium text-foreground-500">{s.unit}</span>
                  </dd>
                  <dd className="mt-1 text-xs text-foreground-500">{s.label}</dd>
                </div>
              ))}
            </dl>
          </div>

          {/* right featured 头版 */}
          <div className="lg:col-span-5 flex flex-col">
            <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-widest text-foreground-500">
              <span className="h-px w-8 bg-accent-500" />
              今日头版 · 正在发酵
            </div>
            <Link
              to={`/gua/${top.id}`}
              className="group relative mt-3 flex flex-col overflow-hidden rounded-2xl border border-background-200 bg-background-50 shadow-soft transition-all hover:shadow-lift cursor-pointer"
            >
              <div className="relative aspect-[16/9] overflow-hidden">
                <img
                  src={top.cover}
                  alt={top.title}
                  className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                />
                <span className={`absolute left-3 top-3 inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-medium ${st.chip}`}>
                  <i className={`${st.icon} text-[13px]`} />
                  {st.label}
                </span>
                <span className="absolute right-3 top-3 rounded-md bg-foreground-950/70 px-2 py-1 text-xs text-primary-200 backdrop-blur-sm">
                  热度 {top.heat}
                </span>
              </div>
              <div className="flex flex-1 flex-col p-5">
                <p className="font-display text-xl font-bold leading-snug text-foreground-950 transition-colors group-hover:text-primary-700 md:text-2xl">
                  {top.title}
                </p>
                <p className="mt-3 line-clamp-3 text-sm leading-relaxed text-foreground-600">{top.summary}</p>
                <div className="mt-4 flex items-center gap-4 border-t border-dashed border-background-200 pt-3 text-xs text-foreground-500">
                  <span className="inline-flex items-center gap-1"><i className="ri-folder-line text-accent-600" />{top.catName}</span>
                  <span className="inline-flex items-center gap-1"><i className="ri-user-3-line text-accent-600" />{top.crowd} 在场</span>
                  <span className="inline-flex items-center gap-1"><i className="ri-time-line text-accent-600" />更新 {top.updated}</span>
                </div>
                <span className="mt-4 inline-flex items-center gap-1.5 text-sm font-semibold text-primary-700">
                  进圈看完整时间线
                  <i className="ri-arrow-right-line transition-transform group-hover:translate-x-1" />
                </span>
              </div>
            </Link>

            <div className="mt-4 rounded-2xl border border-background-200 bg-background-100 p-4">
              <p className="text-xs font-semibold text-foreground-500">⚡ 本瓜田热点关键词</p>
              <div className="mt-2.5 flex flex-wrap gap-2">
                {hotTags.map((t) => (
                  <Link
                    key={t}
                    to={`/#gua?q=${encodeURIComponent(t)}`}
                    className="rounded-md bg-background-50 px-2.5 py-1 text-xs text-foreground-700 transition-colors hover:bg-accent-100 hover:text-accent-900 cursor-pointer"
                  >
                    {t}
                  </Link>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}